/**
 * Main App Component
 *
 * Handles routing and top-level layout for the Life Alert AI platform.
 * Implements code splitting with React.lazy() for optimized loading.
 */

import { lazy, Suspense, useEffect } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import ReactGA from 'react-ga4';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import ExitIntentPopup from './components/ExitIntentPopup';
import { ThemeProvider } from './contexts/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';
import { CrispChat } from './components/CrispChat';
import { OnboardingProvider, OnboardingTrigger } from './components/Onboarding';
import { InstallPrompt, UpdateBanner, OfflineIndicator } from './components/InstallPrompt';

// Lazy-loaded route components for code splitting
const Home = lazy(() => import('./pages/Home'));
const Features = lazy(() => import('./pages/Features'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const About = lazy(() => import('./pages/About'));
const Stories = lazy(() => import('./pages/Stories'));
const PatientDashboard = lazy(() => import('./pages/PatientDashboard'));
const CaregiverDashboard = lazy(() => import('./pages/CaregiverDashboard'));
const Login = lazy(() => import('./pages/Login'));
const FallPrevention = lazy(() => import('./pages/FallPrevention'));
const AICompanionship = lazy(() => import('./pages/AICompanionship'));
const MedicineScanning = lazy(() => import('./pages/MedicineScanning'));
const Blog = lazy(() => import('./pages/Blog'));
const Resources = lazy(() => import('./pages/Resources'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Pricing = lazy(() => import('./pages/Pricing'));
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'));
const FamilyMembers = lazy(() => import('./pages/FamilyMembers'));
const EmergencyContacts = lazy(() => import('./pages/EmergencyContacts'));
const Emergency = lazy(() => import('./pages/Emergency'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-cream-50 dark:bg-night-900" role="status" aria-live="polite">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-sand-200 border-t-coral-600 mx-auto mb-4" aria-hidden="true"></div>
      <p className="text-ink-600 dark:text-gray-400 font-medium">Loading…</p>
    </div>
  </div>
);

function App() {
  const [location] = useLocation();

  // Track page views with Google Analytics
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location, title: document.title });
  }, [location]);

  // Handle email capture from exit intent popup
  const handleExitIntentEmail = (email: string) => {
    // Track email capture in analytics
    ReactGA.event({
      category: 'Lead Generation',
      action: 'Exit Intent Email Capture',
      label: email,
    });
    // In production, you would send this to your email service
    console.log('Exit intent email captured:', email);
  };

  return (
    <ThemeProvider>
      <ErrorBoundary showDetails={import.meta.env.DEV}>
        <OnboardingProvider>
          <div className="min-h-screen bg-cream-50 dark:bg-night-900 transition-colors duration-300">
          {/* Skip to main content link for accessibility */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-btn focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-600"
            aria-label="Skip to main content"
          >
            Skip to main content
          </a>

          {/* Theme Toggle - Fixed position for easy access */}
          <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-4 items-end">
            <ThemeToggle className="shadow-lg" />
            <OnboardingTrigger />
          </div>

          <main id="main-content" role="main" tabIndex={-1}>
          <Suspense fallback={<LoadingFallback />}>
            <Switch>
            <Route path="/" component={Home} />
            <Route path="/features" component={Features} />
            <Route path="/how-it-works" component={HowItWorks} />
            <Route path="/about" component={About} />
            <Route path="/stories" component={Stories} />
            <Route path="/login" component={Login} />
            <Route path="/blog" component={Blog} />
            <Route path="/resources" component={Resources} />
            <Route path="/privacy" component={PrivacyPolicy} />
            <Route path="/terms" component={TermsOfService} />
            <Route path="/pricing" component={Pricing} />
            <Route path="/billing/success" component={BillingSuccess} />
            <Route path="/features/fall-prevention" component={FallPrevention} />
            <Route path="/features/ai-companionship" component={AICompanionship} />
            <Route path="/features/medicine-scanning" component={MedicineScanning} />
            <Route path="/patient/dashboard">
              <ProtectedRoute><PatientDashboard /></ProtectedRoute>
            </Route>
            <Route path="/caregiver/dashboard">
              <ProtectedRoute><CaregiverDashboard /></ProtectedRoute>
            </Route>
            <Route path="/family">
              <ProtectedRoute><FamilyMembers /></ProtectedRoute>
            </Route>
            <Route path="/emergency-contacts">
              <ProtectedRoute><EmergencyContacts /></ProtectedRoute>
            </Route>
            <Route path="/emergency">
              <Emergency />
            </Route>
            <Route>
              {() => (
                <div className="flex items-center justify-center min-h-screen bg-cream-50 dark:bg-night-900 px-6">
                  <div className="text-center max-w-md">
                    <p className="font-display text-7xl font-bold text-gradient-coral mb-4" aria-hidden="true">404</p>
                    <h1 className="!text-3xl mb-3">Page not found</h1>
                    <p className="text-ink-600 dark:text-gray-400 mb-8">The page you're looking for doesn't exist or has moved.</p>
                    <a href="/" className="btn-primary" aria-label="Return to home page">
                      Go Home
                    </a>
                  </div>
                </div>
              )}
            </Route>
          </Switch>
        </Suspense>
      </main>

        {/* Exit Intent Popup - shows when user is about to leave */}
        <ExitIntentPopup onEmailSubmit={handleExitIntentEmail} />

        {/* Live Chat Widget - Crisp */}
        <CrispChat />

        {/* PWA Components */}
        <UpdateBanner />
        <OfflineIndicator />
        <InstallPrompt />
      </div>
        </OnboardingProvider>
    </ErrorBoundary>
  </ThemeProvider>
  );
}

export default App;
